<template>
  <div>
    <EnvironmentUnavailable v-if="envUnavailable" class="mb-6" />

    <!-- Filtres -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
      <div class="grid md:grid-cols-4 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Statut</label>
          <select
            v-model="filters.status"
            @change="reload"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8C0004]"
          >
            <option value="">Tous les statuts</option>
            <option value="CREATED">Créée</option>
            <option value="ASSIGNED">Assignée</option>
            <option value="PICKED_UP">En cours</option>
            <option value="DELIVERED">Livrée</option>
            <option value="CANCELED">Annulée</option>
            <option value="INCIDENT">Incident</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Du</label>
          <input
            v-model="filters.from"
            @change="reload"
            type="date"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8C0004]"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Au</label>
          <input
            v-model="filters.to"
            @change="reload"
            type="date"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8C0004]"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Rechercher</label>
          <input
            v-model="filters.search"
            @keyup.enter="reload"
            placeholder="Référence, destinataire..."
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8C0004]"
          />
        </div>
      </div>
    </div>

    <!-- Tableau -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Référence</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Destinataire</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Livreur</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Montant</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Statut</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Créée le</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-if="loading" v-for="i in 5" :key="`skel-${i}`" class="animate-pulse">
              <td colspan="6" class="px-6 py-4">
                <div class="h-4 bg-gray-100 rounded w-full"></div>
              </td>
            </tr>

            <tr v-else-if="deliveries.length === 0">
              <td colspan="6" class="px-6 py-16 text-center">
                <Icon icon="solar:box-bold" class="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p class="text-gray-500">Aucune livraison dans cet environnement.</p>
                <router-link to="/docs" class="text-sm text-[#8C0004] hover:underline">
                  Créer une livraison depuis la documentation
                </router-link>
              </td>
            </tr>

            <tr v-else v-for="d in deliveries" :key="d.id" class="hover:bg-gray-50 transition-colors">
              <td class="px-6 py-4">
                <p class="text-sm font-medium text-gray-900">{{ d.externalRef || '—' }}</p>
                <p class="text-xs text-gray-400 font-mono">{{ d.id.slice(0, 8) }}</p>
              </td>
              <td class="px-6 py-4">
                <p class="text-sm text-gray-900">{{ d.recipientName || '—' }}</p>
                <p class="text-xs text-gray-500">{{ d.recipientPhone || '' }}</p>
              </td>
              <td class="px-6 py-4 text-sm text-gray-600">
                <span v-if="d.courier">{{ d.courier.firstName }}</span>
                <span v-else class="text-gray-400">Non assigné</span>
              </td>
              <td class="px-6 py-4 text-sm text-gray-900">
                {{ d.amount != null ? `${Math.round(d.amount)} FCFA` : '—' }}
              </td>
              <td class="px-6 py-4">
                <span :class="['px-3 py-1 rounded-full text-xs font-semibold', statusClass(d.status)]">
                  {{ statusLabel(d.status) }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm text-gray-600">{{ formatDate(d.createdAt) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div
        v-if="pagination.totalPages > 1"
        class="px-6 py-4 border-t border-gray-200 flex items-center justify-between"
      >
        <p class="text-sm text-gray-600">
          {{ deliveries.length }} sur {{ pagination.total }} livraison(s)
        </p>
        <div class="flex items-center gap-2">
          <button
            @click="changePage(pagination.page - 1)"
            :disabled="pagination.page <= 1"
            class="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Précédent
          </button>
          <span class="text-sm text-gray-600">
            Page {{ pagination.page }} / {{ pagination.totalPages }}
          </span>
          <button
            @click="changePage(pagination.page + 1)"
            :disabled="pagination.page >= pagination.totalPages"
            class="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Suivant
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Icon } from '@iconify/vue'
import { apiFor, isEnvironmentUnauthorized } from '../services/api'
import EnvironmentUnavailable from '../components/EnvironmentUnavailable.vue'
import { useModeStore } from '../store/mode'

const modeStore = useModeStore()

const deliveries = ref<any[]>([])
const loading = ref(true)
const envUnavailable = ref(false)
const pagination = ref({ total: 0, page: 1, limit: 20, totalPages: 1 })

const filters = ref({ status: '', from: '', to: '', search: '' })

onMounted(load)

async function load() {
  loading.value = true
  try {
    const params: Record<string, any> = {
      page: pagination.value.page,
      limit: pagination.value.limit,
    }
    if (filters.value.status) params.status = filters.value.status
    if (filters.value.from) params.from = filters.value.from
    if (filters.value.to) params.to = new Date(`${filters.value.to}T23:59:59`).toISOString()
    if (filters.value.search) params.search = filters.value.search

    const { data } = await apiFor(modeStore.mode).get('/partner-portal/deliveries', { params })
    deliveries.value = data.deliveries || []
    pagination.value = data.pagination || pagination.value
  } catch (err) {
    envUnavailable.value = isEnvironmentUnauthorized(err)
    console.error('Erreur chargement livraisons:', err)
    deliveries.value = []
  } finally {
    loading.value = false
  }
}

function reload() {
  pagination.value.page = 1
  load()
}

function changePage(page: number) {
  if (page < 1 || page > pagination.value.totalPages) return
  pagination.value.page = page
  load()
}

function statusLabel(status: string): string {
  return (
    {
      CREATED: 'Créée',
      ASSIGNED: 'Assignée',
      PICKED_UP: 'En cours',
      DELIVERED: 'Livrée',
      CANCELED: 'Annulée',
      INCIDENT: 'Incident',
    } as Record<string, string>
  )[status] || status
}

function statusClass(status: string): string {
  return (
    {
      CREATED: 'bg-gray-100 text-gray-800',
      ASSIGNED: 'bg-blue-100 text-blue-800',
      PICKED_UP: 'bg-[#8C0004] bg-opacity-10 text-[#8C0004]',
      DELIVERED: 'bg-green-100 text-green-800',
      CANCELED: 'bg-gray-200 text-gray-600',
      INCIDENT: 'bg-orange-100 text-orange-800',
    } as Record<string, string>
  )[status] || 'bg-gray-100 text-gray-800'
}

function formatDate(value: string): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}
</script>
